import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import BigNumber from 'bignumber.js';
import ReactDOM from "react-dom/client";
import DataGrid from 'devextreme-react/data-grid';
import * as DxLoadPanel from 'devextreme-react/load-panel';
import * as FileSaver from 'file-saver';
import { useSnackbar } from 'notistack';

import { AccessDataContext } from 'contextProviders/AccessDataContext';
import { OverlayContext } from 'contextProviders/OverlayContext';
import { IClientPricelistData } from 'interfaces/Pricelist/IClientPricelistData';
import { UserRoles } from 'enums/UserRoles';
import { PricelistApis } from '../../../../hooks/apis/PricelistApis';
import PageTitle from '../../../components/PageTitle';

import PricelistFilter from '../PricelistFilter/PricelistFilter';
import ClientPricelistDetailView from './components/ClientPricelistDetailView';

import './ClientPricelist.scss';
import 'devextreme/dist/css/dx.light.css';

const ClientPricelist = () => {
  const { securityRoles } = useContext(AccessDataContext);
  const { client } = useParams();
  const pageGrid = useRef(null);
  const { setShowLoadingOverlay } = useContext(OverlayContext);
  const { getClientPricelist, getClientPricelistDownload } = PricelistApis();
  const [pricelist, setPricelist] = useState<IClientPricelistData[] | undefined>(null);
  const [filteredPricelist, setFilteredPricelist] = useState<IClientPricelistData[] | undefined>(null);
  const [textFilter, setTextFilter] = useState<string>('');
  const [selectFilter, setSelectFilter] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const setDownloadErrorSnackbar = useCallback(
    (msg) => {
      enqueueSnackbar && enqueueSnackbar(msg || 'error', { variant: 'error' });
    },
    [enqueueSnackbar]
  );

  const getPricelistTitle = useCallback(() => {
    var title = `${client} Pricelist`;
    if (securityRoles?.filter((f) => f === UserRoles.Admin).length > 0 && pricelist?.length > 0){
      var discount = pricelist[0].Discount;
      const discountText = discount < 0 ? 'BP' + Math.abs(discount) : discount === 0 ? 'B0' : 'BM' + discount;
      title += ` - ${discountText}`;
    }
    return title;
  }, [client, securityRoles, pricelist]);

  useEffect(() => {
    getClientPricelist(client).then((data) => {
      if (!data.Message) {
        setPricelist(data);
      } else {
        // Do something with the error message on the page
      }
      setDataLoading(false);
    });
  }, [client, getClientPricelist]);

  const setCategoryCellTemplate = useCallback(
    (cellElement, info) => {
      const data = pricelist.find((o) => o.CategoryOrder === info.data.key);
      cellElement.prepend(data.Category);
    },
    [pricelist]
  );

  const setGroupCellTemplate = useCallback(
    (cellElement, info) => {
      const data = pricelist.find((o) => o.GroupOrder === info.data.key);
      cellElement.prepend(data.Group);
    },
    [pricelist]
  );

  const setSubCategoryCellTemplate = useCallback(
    (cellElement, info) => {
      const data = pricelist.find((o) => o.SubCategoryOrder === info.data.key);
      cellElement.prepend(data.SubCategory);
    },
    [pricelist]
  );

  const dataRowPrepared = useCallback((e) => {
    if (e?.rowType === 'group') {
      if (e?.groupIndex === 0) {
        e.rowElement.style.background = '#333';
        e.rowElement.style.color = '#FFF';
      }

      if (e?.groupIndex === 1) {
        e.rowElement.style.background = '#015b40';
        e.rowElement.style.color = '#FFF';
      }

      if (e?.groupIndex === 2) {
        e.rowElement.style.background = '#569149';
        e.rowElement.style.color = '#FFF';
      }
    }
  }, []);

  const exportPricelist = useCallback(() => {
    setShowLoadingOverlay(true);
    const fileName = getPricelistTitle()?.replace(/[^a-z0-9]/gi, ' ')?.replace(/\s\s+/g, ' ')?.replace(/[^a-z0-9]/gi, '_');

    getClientPricelistDownload(client)
      .then((response) => {
        FileSaver.saveAs(response, fileName);
      })
      .finally(() => setShowLoadingOverlay(false))
      .catch((error) => setDownloadErrorSnackbar(`Download Failed: ${error}`));
  }, [client, getPricelistTitle, getClientPricelistDownload, setDownloadErrorSnackbar, setShowLoadingOverlay]);

  useEffect(() => {
      if (!pricelist) return;
    
      let filteredData = [...pricelist];
      console.log(selectFilter);
      console.log(textFilter);
      // Apply select filters (Group filtering)
      if (selectFilter?.length > 0) {
        filteredData = filteredData.filter((item) => selectFilter.includes(item.Group));
      }
    
      // Apply text search (mimicking DataGrid.searchByText)
      if (textFilter) {
        const lowerCaseSearch = textFilter.toLowerCase();
        
        filteredData = filteredData.filter((item) => 
          Object.keys(item).some((key) => 
            typeof item[key] === "string" && item[key]?.toLowerCase().includes(lowerCaseSearch)
          )
        );
      }
    
      setFilteredPricelist(filteredData);
    }, [pricelist, textFilter, selectFilter]);

  const ClientPricelistDetailViewWrapper = ({ data }: { data?: IClientPricelistData }) => {
    return <ClientPricelistDetailView {...data} />;
  };

  return (
    <>
      <PageTitle pageTitle={getPricelistTitle()} />

      <Box className="pricelist">
        <PricelistFilter
          exportPricelist={exportPricelist}
          textFilter={textFilter}
          setTextFilter={setTextFilter}
          setSelectFilter={setSelectFilter}
        />

        <DxLoadPanel.LoadPanel
          height={130}
          width={250}
          message={`Loading Pricelist data for ${client}`}
          visible={dataLoading}
          position={{ of: '#clientPricelist' }}
        />

        <DataGrid
          ref={pageGrid}
          id="clientPricelist"
          height={780}
          dataSource={filteredPricelist}
          allowColumnReordering={true}
          rowAlternationEnabled={true}
          showBorders={false}
          wordWrapEnabled={true}
          hoverStateEnabled={false}
          onRowPrepared={dataRowPrepared}
          paging={{enabled: true, pageSize: 25}}
          scrolling={{scrollByContent: false, scrollByThumb: false}}
          groupPanel={{visible: false}}
          headerFilter={{visible: true}}
          selection={{mode: 'single'}}
          loadPanel={{enabled: false}}
          sorting={{mode:"multiple"}}
          columns={[
            {dataField: "Group", groupCellTemplate: setGroupCellTemplate, calculateGroupValue: "GroupOrder", groupIndex: 0, dataType: "string"},
            {dataField: "Category", groupCellTemplate: setCategoryCellTemplate, calculateGroupValue: "CategoryOrder", groupIndex: 1, dataType: "string"},
            {dataField: "SubCategory", groupCellTemplate: setSubCategoryCellTemplate, calculateGroupValue: "SubCategoryOrder", groupIndex: 2, dataType: "string"},
            {dataField: "EnviroCode", caption: "Code", sortOrder: "asc", width: 100, dataType: "string"},
            {dataField: "Description", minWidth: 300, dataType: "string"},
            {dataField: "Matrix", alignment: "center", dataType: "string", width: 100},
            {
              dataField: "Price",
              caption: "Price",
              alignment: "right",
              dataType: "number",
              width: 80,
              cellTemplate: (container, options) => {
                const value = options.data?.FinalPrice;
                const formattedPrice = value
                  ? `$${new BigNumber(value).dp(2, BigNumber.ROUND_HALF_UP).toFixed(2)}`
                  : "POA";
          
                  const root = ReactDOM.createRoot(container);
                  root.render(<Box className="priceBox">{formattedPrice}</Box>);
              }},
              {dataField: "Analysis", visible: false, dataType: "string"},
              {dataField: "Analytes", visible: false, dataType: "string"},
              {dataField: "AllAnalysis", visible: false, dataType: "string"},
              {
                dataField: "AnalysisComments",
                caption: "Analysis Comments and References",
                dataType: "string",
                allowFiltering: false,
                width: 600,
                cellTemplate: (container, options) => {
                  const value = options.data?.AnalysisComments;
                  const root = ReactDOM.createRoot(container);
                  root.render(<Box className="analysisComments">{value}</Box>);
              }},  
          ]}
          masterDetail={{
            enabled: true,
            template: (container, options) => {
              while (container.firstChild) {
                container.removeChild(container.firstChild);
              }
        
              const root = ReactDOM.createRoot(container);
              root.render(<ClientPricelistDetailViewWrapper data={options.data} />);
            }
          }}
        />

        <Box className="disclaimerText">* Please note listed references do not imply NATA accreditation to said reference methods or standards.</Box>
      </Box>
    </>
  );
};

export default React.memo(ClientPricelist);

